import React, { useMemo, useState } from 'react'
import { Stack, Typography, Box } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@apollo/client'
import TemporaryIcon from 'components/global/TemporaryIcon'
import { useSites } from 'store'
import { useGetStatsTimeRange } from '../store'
import { convertDateRange } from '../hooks/use-traffic'
import OPERATION_FIND_TRAFFIC_BY_DEVICE from '../../operations-gql/queries'
import TrafficDistributionWidgetSkeleton from '../../Skeletons/TrafficDistributionWidgetSkeleton'
import WidgetContainer from './WidgetContainer'

const DEVICE_CONFIG = {
  mobile: { color: '#1E55AF', order: 0 },
  tablet: { color: '#5791F0', order: 1 },
  desktop: { color: '#A1C2F7', order: 2 }
} as const

type DeviceType = keyof typeof DEVICE_CONFIG

const TrafficDistributionWidget = () => {
  const { t } = useTranslation()
  const [{ selected }] = useSites()
  const [selectedTimeRange] = useGetStatsTimeRange()
  const [hoveredItem, setHoveredItem] = useState<DeviceType | null>(null)
  const { startsAt, endsAt } = convertDateRange(selectedTimeRange)

  const { loading, error, data } = useQuery(OPERATION_FIND_TRAFFIC_BY_DEVICE, {
    variables: {
      site_sf_ids: selected ? selected.map(({ sf_id }) => sf_id) : [],
      created_at_start: startsAt,
      created_at_end: endsAt,
    },
  })

  const trafficData = useMemo(() => {
    if (!data?.trafficByDevice?.length) return []

    const processedData = data.trafficByDevice
      .filter(item => item.percentage > 0 && item.key in DEVICE_CONFIG)
      .map(item => ({
        type: item.key as DeviceType,
        percentage: Math.round(item.percentage * 10) / 10,
        color: DEVICE_CONFIG[item.key as DeviceType].color,
        order: DEVICE_CONFIG[item.key as DeviceType].order
      }))
      .sort((a, b) => a.order - b.order)

    return processedData
  }, [data])

  return (
    <WidgetContainer
      width={{ xs: '100%', sm: '50%', md: '25%' }}
      minHeight="100px"
      loading={loading}
      LoadingComponent={TrafficDistributionWidgetSkeleton}
      error={!!error}
      isEmpty={!trafficData.length}
      emptyIcon="at"
      emptyMessage={error ? t('stats.traffic.widgets.traffic_distribution.error') : t('stats.traffic.widgets.traffic_distribution.no_data')}
    >
      <Stack spacing={1.5} alignItems="center" sx={{ width: '100%' }}>
        <Stack spacing={0.5} alignItems="center" pb={1}>
          <TemporaryIcon
            iconlabel="laptop-mobile"
            iconsize="16px"
            iconcolor="#1976d2"
          />
          <Typography
            sx={{
              fontWeight: 600,
              fontSize: '12px',
              textTransform: 'uppercase',
              textAlign: 'center'
            }}
          >
            {t('stats.device_traffic_distribution.title')}
          </Typography>
        </Stack>

        <Box sx={{
          display: 'flex',
          height: '22px',
          borderRadius: '6px',
          overflow: 'visible',
          width: '100%',
          alignItems: 'center'
        }}>
          {trafficData.map((item, index) => (
            <Box
              key={item.type}
              onMouseEnter={() => setHoveredItem(item.type)}
              onMouseLeave={() => setHoveredItem(null)}
              sx={{
                backgroundColor: item.color,
                width: `${item.percentage}%`,
                height: '16px',
                marginLeft: index > 0 ? '1px' : 0,
                borderTopLeftRadius: index === 0 ? '6px' : 0,
                borderBottomLeftRadius: index === 0 ? '6px' : 0,
                borderTopRightRadius: index === trafficData.length - 1 ? '6px' : 0,
                borderBottomRightRadius: index === trafficData.length - 1 ? '6px' : 0,
                animation: `slideInBar-${index} 0.5s ease-out forwards`,
                animationDelay: `${index * 0.15}s`,
                opacity: 0,
                [`@keyframes slideInBar-${index}`]: {
                  '0%': {
                    opacity: 0,
                    width: '0%',
                    marginLeft: index > 0 ? '0px' : 0
                  },
                  '100%': {
                    opacity: 1,
                    width: `${item.percentage}%`,
                    marginLeft: index > 0 ? '1px' : 0
                  }
                },
                '&:hover': {
                  margin: '1px',
                  height: '18px',
                  width: `calc(${item.percentage}% - 2px)`,
                  marginLeft: index > 0 ? '2px' : '1px'
                }
              }}
            />
          ))}
        </Box>

        <Stack direction="row" spacing={1.5} sx={{ flexWrap: 'wrap', justifyContent: 'center' }}>
          {trafficData.map((item) => (
            <Stack key={item.type} direction="row" alignItems="center" spacing={0.5}>
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  backgroundColor: item.color,
                  opacity: hoveredItem === null ? 1 : hoveredItem === item.type ? 1 : 0.3
                }}
              />
              <Typography
                sx={{
                  fontSize: '10px',
                  fontWeight: 400,
                  color: '#292929',
                  opacity: hoveredItem === null ? 1 : hoveredItem === item.type ? 1 : 0.3
                }}
              >
                {t(`stats.device_traffic_distribution.${item.type}`)} {item.percentage}%
              </Typography>
            </Stack>
          ))}
        </Stack>
      </Stack>
    </WidgetContainer>
  )
}

export default TrafficDistributionWidget